import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, BookOpen } from 'lucide-react';

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    __QB_ADSENSE_CLIENT_ID__?: string;
    __QB_ADSENSE_SLOTS__?: Record<string, string>;
  }
}

export interface AdSenseBannerProps {
  slot?: string;
  placement?: 'home' | 'quiz-setup' | 'quiz-results' | 'rankings';
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal';
  className?: string;
}

const DEFAULT_SLOTS: Record<string, string> = {
  home:
    (import.meta as any).env?.VITE_ADSENSE_SLOT_HOME ||
    '1029384756',
  'quiz-setup':
    (import.meta as any).env?.VITE_ADSENSE_SLOT_QUIZ_SETUP ||
    '2039485761',
  'quiz-results':
    (import.meta as any).env?.VITE_ADSENSE_SLOT_QUIZ_RESULTS ||
    '3049586712',
  rankings:
    (import.meta as any).env?.VITE_ADSENSE_SLOT_RANKINGS ||
    '4059687123',
};

function resolveAdSenseClientId(): string {
  const fromVite = (import.meta as any).env?.VITE_ADSENSE_CLIENT_ID?.trim();
  const fromWindow =
    typeof window !== 'undefined' ? window.__QB_ADSENSE_CLIENT_ID__?.trim() : '';
  const candidate = fromVite || fromWindow || '';
  if (
    !candidate ||
    candidate === 'YOUR_ADSENSE_CLIENT_ID' ||
    candidate === 'ca-pub-XXXXXXXXXXXXXXXX'
  ) {
    return '';
  }
  return candidate.startsWith('ca-pub-') ? candidate : `ca-pub-${candidate}`;
}

function ensureAdSenseScriptLoaded(clientId: string) {
  if (typeof document === 'undefined' || !clientId) return;
  const existing = document.querySelector(
    'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
  );
  if (existing) return;

  try {
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(
      clientId
    )}`;
    script.onerror = () => {
      // Gracefully handle ad blockers or offline mode without breaking the app
    };
    document.head.appendChild(script);
  } catch {
    // Ignore script injection errors
  }
}

export const AdSenseBanner: React.FC<AdSenseBannerProps> = ({
  slot,
  placement = 'home',
  format = 'auto',
  className = '',
}) => {
  const insRef = useRef<HTMLModElement | null>(null);
  const pushedRef = useRef(false);
  const [adStatus, setAdStatus] = useState<'ready' | 'filled' | 'fallback'>(
    'ready'
  );

  const clientId = resolveAdSenseClientId();
  const resolvedSlot =
    slot ||
    (typeof window !== 'undefined' &&
      window.__QB_ADSENSE_SLOTS__?.[placement]) ||
    DEFAULT_SLOTS[placement] ||
    '1029384756';

  useEffect(() => {
    if (!clientId) {
      setAdStatus('fallback');
      return;
    }

    ensureAdSenseScriptLoaded(clientId);

    const insEl = insRef.current;
    if (!insEl || pushedRef.current) return;

    // Avoid pushing twice to the same <ins> element in React 19 StrictMode
    if (insEl.getAttribute('data-adsbygoogle-status')) {
      pushedRef.current = true;
      setAdStatus('filled');
      return;
    }

    const timer = window.setTimeout(() => {
      try {
        if (
          insRef.current &&
          !insRef.current.getAttribute('data-adsbygoogle-status')
        ) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          pushedRef.current = true;
          setAdStatus('filled');
        }
      } catch {
        setAdStatus('fallback');
      }
    }, 150);

    return () => window.clearTimeout(timer);
  }, [clientId, resolvedSlot]);

  return (
    <aside
      aria-label="Sponsored Advertisement"
      data-testid={`adsense-banner-${placement}`}
      data-ad-placement={placement}
      className={`w-full max-w-full overflow-hidden rounded-2xl border border-[#262626] bg-[#141414] p-3 sm:p-4 shadow-xs ${className}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2 text-[10px] font-mono uppercase tracking-wider text-[#737373]">
        <span className="inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-[#737373] shrink-0" />
          <span>Sponsored · Google AdSense</span>
        </span>
        <span>Quick!Bible Sanctuary Partner</span>
      </div>

      {/* Official Google AdSense <ins> unit */}
      <div className="w-full min-h-[76px] flex items-center justify-center overflow-hidden rounded-xl bg-[#141414] border border-dashed border-[#262626] px-3 py-2.5">
        {clientId ? (
          <ins
            ref={insRef}
            className="adsbygoogle block w-full"
            style={{ display: 'block', minHeight: '70px', width: '100%' }}
            data-ad-client={clientId}
            data-ad-slot={resolvedSlot}
            data-ad-format={format}
            data-full-width-responsive="true"
          />
        ) : null}

        {(!clientId || adStatus === 'fallback') && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full max-w-3xl text-center sm:text-left px-2 py-1">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-[#1C1C1C] text-[#A3A3A3] flex items-center justify-center shrink-0">
                <BookOpen className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[#FAFAFA] truncate">
                  Quick!Bible Global Scripture Study &amp; Live Fellowships
                </div>
                <div className="text-[11px] text-[#A3A3A3] leading-snug">
                  Supporting free multilingual Bible quizzes, church tournaments, and auditorium LIVE events worldwide.
                </div>
              </div>
            </div>
            <span className="text-[10px] font-mono px-2.5 py-1 rounded-lg bg-[#1C1C1C] text-[#FAFAFA] border border-[#262626] shrink-0">
              Ad Slot · {placement}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
};
